'use client';

import { IArea, IStoreLists } from '@/interfaces/store';
import { ILocation, SetString } from '@/interfaces/location';
import { resolveThanaById } from '@/helpers/address-helper';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

export interface UseLocationModalStateArgs {
  isMyLocation: boolean;
  myLocation: IArea;
  showLocationModal: boolean;
  /** Bulk location data. Same shape consumed by `resolveThanaById`. */
  allLocationList?: { Thanas?: ILocation[] | null; Districts?: any; Divisions?: any } | null;
  thanaData?: ILocation[] | null;
  setDivisionId: SetString;
  setDistrictId: SetString;
  setThanaId: SetString;
  setSelectedStore: (s: IStoreLists) => void;
}

export interface UseLocationModalState {
  // Search input (Bopis only)
  storeSearchValue: string;
  setStoreSearchValue: Dispatch<SetStateAction<string>>;
  findStore: (data: { store?: string[] } | any) => void;

  // Picked pins — TWO distinct coordinate shapes (audited in Phase 8)
  // - pickedLocation:        canonical {latitude, longitude} for Express
  // - bopisPickedLocation:   Map-shape  {longitude, latitude} for Bopis
  pickedLocation: { latitude: number; longitude: number } | null;
  setPickedFromMap: (loc: { latitude: number; longitude: number } | null) => void;
  bopisPickedLocation: { longitude: number; latitude: number } | null;
  setBopisPickedLocation: Dispatch<SetStateAction<{ longitude: number; latitude: number } | null>>;

  invalidMapPick: boolean;
  setInvalidMapPick: Dispatch<SetStateAction<boolean>>;

  // Semantic ops
  reset: () => void;
  /** Internal reset only — does NOT call setShowLocationModal. */
  closeModal: () => void;
}

export const useLocationModalState = ({
  isMyLocation,
  myLocation,
  showLocationModal,
  allLocationList,
  thanaData,
  setDivisionId,
  setDistrictId,
  setThanaId,
  setSelectedStore,
}: UseLocationModalStateArgs): UseLocationModalState => {
  const [storeSearchValue, setStoreSearchValue] = useState<string>('');
  const [pickedLocation, setPickedLocationState] = useState<{ latitude: number; longitude: number } | null>(null);
  const [bopisPickedLocation, setBopisPickedLocationState] = useState<{ longitude: number; latitude: number } | null>(
    null,
  );
  const [invalidMapPick, setInvalidMapPick] = useState<boolean>(false);

  // Ref (not state) so re-renders from setters don't snap the pin back.
  // `wasOpenRef` tracks the previous open state and re-arms hydration on
  // every fresh open.
  const hydratedRef = useRef(false);
  const wasOpenRef = useRef(false);

  const findStore = (data: any) => {
    if (data?.store?.[0]?.trim().length > 0) {
      setStoreSearchValue(data.store[0].trim());
    } else {
      setStoreSearchValue('');
    }
  };

  // Hydrate dropdowns + map once per modal-open.
  useEffect(() => {
    if (!isMyLocation) {
      return;
    }
    if (showLocationModal && !wasOpenRef.current) {
      // Modal just opened (closed → opened) → require re-hydration.
      hydratedRef.current = false;
    }
    wasOpenRef.current = showLocationModal;

    if (!showLocationModal) {
      return;
    }

    if (hydratedRef.current) {
      return;
    }

    if (!myLocation?.value || myLocation.value === '-1') {
      return;
    }

    const thanasList = allLocationList?.Thanas as ILocation[] | undefined;
    const districtsList = allLocationList?.Districts as unknown as
      | Array<{ Id: string; ParentId: string }>
      | undefined;
    const divisionsList = allLocationList?.Divisions as unknown as
      | Array<{ Id: string; ParentId: string }>
      | undefined;

    if (!thanasList?.length || !districtsList?.length || !divisionsList?.length) {
      return;
    }

    // Use the same fallback chain as the dropdown so the saved thana
    // (possibly only present in `thanaData`, not the bulk list) is found.
    const thana = resolveThanaById(myLocation.value, thanaData, allLocationList ?? undefined);

    if (!thana) {
      return;
    }

    const district = districtsList.find((d) => d.Id === thana.ParentId);
    const division = district ? divisionsList.find((v) => v.Id === district.ParentId) : undefined;

    setDistrictId(thana.ParentId);
    setDivisionId(division?.Id ?? '-1');
    setThanaId(thana.Id);

    const lat = parseFloat(thana.Lat);
    const lng = parseFloat(thana.Long);

    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      setPickedLocationState({ latitude: lat, longitude: lng });
    }

    hydratedRef.current = true;
  }, [showLocationModal, allLocationList, myLocation, isMyLocation, thanaData]);

  const setPickedFromMap = (loc: { latitude: number; longitude: number } | null) => {
    setPickedLocationState(loc);
  };

  const setBopisPickedLocation: Dispatch<SetStateAction<{ longitude: number; latitude: number } | null>> = (
    value,
  ) => {
    setBopisPickedLocationState(value);
  };

  const reset = () => {
    setStoreSearchValue('');
    setInvalidMapPick(false);
    setBopisPickedLocationState(null);
    setPickedLocationState(null);
    setSelectedStore({} as IStoreLists);
    setDivisionId('-1');
    setDistrictId('-1');
    setThanaId('-1');
  };

  // Internal reset only — parent owns setShowLocationModal notification.
  const closeModal = () => {
    reset();
  };

  return {
    storeSearchValue,
    setStoreSearchValue,
    findStore,
    pickedLocation,
    setPickedFromMap,
    bopisPickedLocation,
    setBopisPickedLocation,
    invalidMapPick,
    setInvalidMapPick,
    reset,
    closeModal,
  };
};

export default useLocationModalState;
